/** Inspection-report library + language picker — Phase 2 (per-language,
 * one-time, eIDAS-timestamped reports).
 *
 * Replaces the old two-button (signed/unsigned) UI. Shows the languages a
 * timestamped report already exists in (instant download) and an "Add a
 * language" / "Generate report" picker. The first generation of a language
 * is eIDAS-timestamped + stored server-side; every later download returns
 * that exact stored document — so each language costs at most one timestamp.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiPaths, api } from "@/lib/api";
import { SUPPORTED_LOCALES } from "@/lib/i18n";

import { formatEtaTime, useReportAnchorStatus } from "./useReportAnchorStatus";

type LanguagesResp = {
  default: string;
  available: string[];
  translated: string[];
  /** lang -> when that language's report was sealed (ISO). */
  generated: Record<string, string>;
  /** Languages sealed BEFORE the questionnaire last changed. The
   *  document is not wrong about what it attests to — it is wrong about
   *  being complete, and only comparing two timestamps would show it. */
  stale?: string[];
  qa_last_changed_at?: string | null;
};

type ErrorBody = { reason?: string; detail?: string };

const localeLabel = (code: string) =>
  SUPPORTED_LOCALES.find((l) => l.code === code)?.label ?? code.toUpperCase();

async function readErrorBody(data: unknown): Promise<ErrorBody | null> {
  if (!data) return null;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()) as ErrorBody;
    } catch {
      return null;
    }
  }
  if (typeof data === "object") return data as ErrorBody;
  return null;
}


export function InspectionReportPanel({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [picking, setPicking] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set(["fr"]));
  const [busyLang, setBusyLang] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const anchor = useReportAnchorStatus(sessionId);
  const locked = anchor.data ? !anchor.data.ready : false;

  const langsKey = ["inspection-report-langs", sessionId];
  const langs = useQuery({
    queryKey: langsKey,
    queryFn: async () =>
      (
        await api.get<LanguagesResp>(
          ApiPaths.sessionInspectionReportLanguages(sessionId),
        )
      ).data,
  });

  const generated = langs.data?.generated ?? {};
  const generatedCodes = Object.keys(generated).sort();
  const stale = new Set(langs.data?.stale ?? []);
  const available = langs.data?.available ?? [];
  // backlog §9.1 (2026-09-08) — separate from downloadLang's busy state
  // so the preview button and the (disabled, while locked) language
  // list never fight over one spinner.
  const [previewPending, setPreviewPending] = useState(false);

  const downloadPreview = async () => {
    setPreviewPending(true);
    setErr(null);
    try {
      const res = await api.get<Blob>(
        ApiPaths.sessionInspectionReportPdf(sessionId, true),
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `inspection-${sessionId}-preview.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const e2 = e as { response?: { data?: unknown } };
      const body = await readErrorBody(e2?.response?.data);
      setErr(body?.detail || t("session.downloadReportError"));
    } finally {
      setPreviewPending(false);
    }
  };

  const downloadLang = async (lang: string, refresh = false) => {
    setBusyLang(lang);
    setErr(null);
    try {
      const res = await api.get<Blob>(
        ApiPaths.sessionInspectionReportSignedPdf(sessionId, lang, refresh),
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `inspection-${sessionId}-${lang}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      await qc.invalidateQueries({ queryKey: langsKey });
      return true;
    } catch (e) {
      const e2 = e as { response?: { status?: number; data?: unknown } };
      const status = e2?.response?.status;
      const body = await readErrorBody(e2?.response?.data);
      if (
        status === 425 ||
        (body as ErrorBody & { detail?: string })?.detail ===
          "report_awaiting_anchor"
      ) {
        await anchor.refetch();
        setErr(t("session.reportAwaitingAnchor"));
      } else if (status === 409 && body?.reason === "kyc_required") {
        setErr(body.detail || t("session.reportKybRequired"));
      } else if (status === 502 || body?.reason === "signing_failed") {
        setErr(body?.detail || t("session.reportSigningUnavailable"));
      } else {
        setErr(body?.detail || t("session.downloadReportError"));
      }
      return false;
    } finally {
      setBusyLang(null);
    }
  };

  const generateSelected = async () => {
    for (const lang of selected) {
      const ok = await downloadLang(lang);
      if (!ok) return; // stop on first failure (e.g. KYB gate)
    }
    setPicking(false);
    setSelected(new Set(["fr"]));
  };

  const toggle = (code: string, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(code);
    else next.delete(code);
    setSelected(next);
  };

  if (locked) {
    const etaTime = formatEtaTime(anchor.data?.next_batch_eta);
    return (
      <div className="space-y-2">
        <div className="text-xs font-medium text-nb-text-2">
          {t("session.reportLibraryTitle")}
        </div>
        <div className="rounded border border-nb-border p-2">
          <p className="text-sm text-nb-text-2">{t("session.reportLocked")}</p>
          <p className="mt-1 text-xs text-cvp-mute" role="status">
            {etaTime
              ? t("session.reportAwaitingAnchorEta", { time: etaTime })
              : t("session.reportAwaitingAnchor")}
          </p>
        </div>
        {/* backlog §9.1 — a watermarked, unsigned preview the operator
            can hand over right away, sitting NEXT TO the lock UI above
            rather than replacing it: the certified report is still the
            goal, this is just something to give the customer during
            the ~15-20 min wait. */}
        <button
          type="button"
          className="btn-ghost w-full text-sm"
          onClick={downloadPreview}
          disabled={previewPending}
          data-testid="report-download-preview"
        >
          {previewPending
            ? t("session.reportDownloadingPreview")
            : t("session.reportDownloadPreview")}
        </button>
        <p className="text-xs text-nb-text-3">{t("session.reportPreviewNote")}</p>
        {err && (
          <p className="text-xs text-nb-warn" role="alert">
            {err}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-nb-text-2">
        {t("session.reportLibraryTitle")}
      </div>

      {generatedCodes.length > 0 ? (
        <ul className="space-y-1">
          {generatedCodes.map((code) => (
            <li key={code}>
              <button
                type="button"
                className="btn-ghost w-full text-sm flex items-center justify-between"
                onClick={() => downloadLang(code)}
                disabled={busyLang !== null}
                data-testid={`report-download-${code}`}
              >
                <span>
                  {localeLabel(code)}
                  {stale.has(code) && (
                    <span
                      className="ml-2 rounded-full border border-nb-warn/50 bg-nb-warn/10 px-1.5 py-0.5 text-[10px] font-medium text-nb-warn"
                      data-testid={`report-stale-${code}`}
                    >
                      {t("session.reportStaleBadge")}
                    </span>
                  )}
                </span>
                <span className="text-nb-text-2">
                  {busyLang === code
                    ? t("session.downloadingReport")
                    : "↓ PDF"}
                </span>
              </button>
              {/* Offered only where it is needed. A regenerate button on
                  a current report is an invitation to replace a sealed
                  document for no reason — and the superseded one may
                  already be in a third party's hands. */}
              {stale.has(code) && (
                <div className="mt-0.5 pl-2">
                  <p className="text-[11px] text-nb-warn">
                    {t("session.reportStaleHint")}
                  </p>
                  <button
                    type="button"
                    className="text-[11px] text-nb-mint underline hover:no-underline"
                    onClick={() => downloadLang(code, true)}
                    disabled={busyLang !== null}
                    data-testid={`report-regenerate-${code}`}
                  >
                    {busyLang === code
                      ? t("session.reportRegenerating")
                      : t("session.reportRegenerate")}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-nb-text-2">{t("session.reportNoneYet")}</p>
      )}

      {!picking ? (
        <button
          type="button"
          className="btn-primary w-full text-sm"
          onClick={() => setPicking(true)}
          data-testid="report-generate-open"
        >
          {generatedCodes.length
            ? t("session.reportAddLanguage")
            : t("session.generateReport")}
        </button>
      ) : (
        <div className="rounded border border-nb-border p-2 space-y-2">
          <p className="text-xs text-nb-text-2">
            {t("session.reportTimestampNotice")}
          </p>
          <div className="grid grid-cols-2 gap-1 max-h-40 overflow-auto">
            {available.map((code) => {
              const already = generatedCodes.includes(code);
              return (
                <label
                  key={code}
                  className={`flex items-center gap-1 text-xs ${
                    already ? "opacity-50" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    disabled={already || busyLang !== null}
                    checked={already || selected.has(code)}
                    onChange={(e) => toggle(code, e.target.checked)}
                  />
                  {localeLabel(code)}
                  {already ? " ✓" : ""}
                </label>
              );
            })}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-primary text-sm flex-1"
              onClick={generateSelected}
              disabled={busyLang !== null || selected.size === 0}
              data-testid="report-generate-confirm"
            >
              {busyLang
                ? t("session.generatingReport", { lang: localeLabel(busyLang) })
                : t("session.generateReport")}
            </button>
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => setPicking(false)}
              disabled={busyLang !== null}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}

      {err && (
        <p className="text-xs text-nb-warn" role="alert">
          {err}
        </p>
      )}
    </div>
  );
}
